/**
 * Where a guest goes back to once they have signed up.
 *
 * `/auth` used to send everyone to My Meals, so a guest stopped at the camera
 * by "create an account to scan" landed three taps away from the scan they had
 * asked for. The sign-up prompt now names the screen it came from in `next`,
 * and this decides whether that name may be followed.
 *
 * The value ends up in `router.replace`, so it is matched against a short
 * allowlist rather than trusted: an exact screen, or a recipe page whose id is
 * plain id characters. Anything else (another origin, `//host`, a query, a
 * path we did not put there) is ignored and the caller falls back to the app.
 * Invite links have their own, stricter hand-off in `resolvePostAuthRoute`,
 * which always wins.
 */
const RETURN_SCREENS = new Set(['/inventory', '/recipes']);
const RECIPE_PAGE = /^\/recipe-detail\/[A-Za-z0-9_-]{1,128}$/;

export function resolveReturnRoute(nextParam: string | string[] | undefined): string | null {
  const next = Array.isArray(nextParam) ? nextParam[0] : nextParam;
  if (typeof next !== 'string') return null;
  const route = next.trim();
  if (RETURN_SCREENS.has(route) || RECIPE_PAGE.test(route)) return route;
  return null;
}

/** The `/auth` link that comes back to `returnTo` (when it is one we accept). */
export function authHrefReturningTo(returnTo: string | null | undefined): string {
  const route = resolveReturnRoute(returnTo ?? undefined);
  return route ? `/auth?next=${encodeURIComponent(route)}` : '/auth';
}
